import { createI18n } from 'vue-i18n'
import * as Sentry from '@sentry/vue'
import nlMessages from '@/locales/nl.json'

/**
 * Supported locales for the application.
 * Dutch (nl) is the default, English (en) is secondary.
 */
export type SupportedLocale = 'nl' | 'en'
export const SUPPORTED_LOCALES: readonly SupportedLocale[] = ['nl', 'en'] as const
export const DEFAULT_LOCALE: SupportedLocale = 'nl'

/**
 * Type for locale messages based on the Dutch locale file.
 */
export type MessageSchema = typeof nlMessages

/**
 * Named datetime formats used by `d()` call sites.
 * Keep entries here aligned with every named format passed to `d(date, '<name>')`
 * so vue-i18n never falls through to its missing-key handler (Sentry SCRIVIO-FE-6B).
 */
const datetimeFormats = {
  nl: {
    short: {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    },
    long: {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
  },
  en: {
    short: {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    },
    long: {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
  },
} as const

/**
 * Module augmentation for type-safe translations.
 */
declare module 'vue-i18n' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface DefineLocaleMessage extends MessageSchema {}
}

/**
 * Cache to track which locales have been loaded.
 */
const loadedLocales = new Set<SupportedLocale>()

/**
 * Tracks which `locale:key` combinations have already been reported to Sentry,
 * so a missing key on a hot component doesn't flood the project on every render.
 */
const reportedMissingKeys = new Set<string>()

/**
 * Load a locale file dynamically using Vite's dynamic import.
 */
async function loadLocaleMessages(locale: SupportedLocale): Promise<MessageSchema> {
  const messages = await import(`@/locales/${locale}.json`)
  return messages.default as MessageSchema
}

/**
 * The shared i18n instance, initialized lazily.
 */
let i18nInstance: ReturnType<typeof createI18n> | null = null

/**
 * Set the active locale and load its messages if needed.
 */
export async function setLocale(locale: SupportedLocale): Promise<void> {
  if (!i18nInstance) {
    throw new Error('i18n not initialized. Call setupI18n first.')
  }

  if (!loadedLocales.has(locale)) {
    const messages = await loadLocaleMessages(locale)
    i18nInstance.global.setLocaleMessage(locale, messages)
    loadedLocales.add(locale)
  }

  // @ts-expect-error - vue-i18n types are overly strict for locale assignment
  i18nInstance.global.locale.value = locale
  document.documentElement.setAttribute('lang', locale)
}

/**
 * vue-i18n `missing` callback. Fires only when no fallback resolved the key,
 * so users would otherwise see the raw key string. Forwards to Sentry at
 * `warning` level, deduplicated per `locale:key` for the session.
 */
function reportMissingKey(locale: string, key: string, _instance?: unknown, type?: string): void {
  const dedupKey = `${locale}:${key}`
  if (reportedMissingKeys.has(dedupKey)) return
  reportedMissingKeys.add(dedupKey)
  Sentry.captureMessage(`Missing i18n key: ${key}`, {
    level: 'warning',
    tags: { locale, i18nMissingType: type },
    extra: { key, locale },
  })
}

/**
 * Create and configure the i18n instance with lazy loading.
 * Only loads the default locale (Dutch) initially; other locales are loaded on demand.
 */
export function setupI18n() {
  // Load Dutch synchronously (it's the default and most common)
  loadedLocales.add(DEFAULT_LOCALE)

  i18nInstance = createI18n({
    legacy: false,
    locale: DEFAULT_LOCALE,
    fallbackLocale: DEFAULT_LOCALE,
    messages: {
      nl: nlMessages,
    },
    datetimeFormats,
    missingWarn: import.meta.env.DEV,
    fallbackWarn: import.meta.env.DEV,
    missing: reportMissingKey,
  })

  document.documentElement.setAttribute('lang', DEFAULT_LOCALE)

  return i18nInstance
}

/**
 * Check if a locale is supported.
 */
export function isLocaleSupported(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale)
}

/**
 * Get display name for a locale.
 */
export function getLocaleDisplayName(locale: SupportedLocale): string {
  const displayNames: Record<SupportedLocale, string> = {
    nl: 'Nederlands',
    en: 'English',
  }
  return displayNames[locale]
}

/**
 * Get the global translation function for use outside of Vue components.
 * This is useful for Pinia stores or utility functions that need to translate strings.
 *
 * Bind it once where the store is set up — `const t = getGlobalT()` at the top
 * of the `defineStore` callback — rather than calling it inside a `catch`. It
 * throws when i18n is not initialized, and inside a catch block that would turn
 * a handled API failure into an unhandled one.
 *
 * @throws Error if i18n is not initialized (call setupI18n first)
 */
export function getGlobalT(): (key: string, params?: Record<string, unknown> | number) => string {
  if (!i18nInstance) {
    throw new Error('i18n not initialized. Call setupI18n first.')
  }
  return i18nInstance.global.t
}

/**
 * The active locale, for the same callers {@link getGlobalT} serves: a store or a
 * composable that formats a number or a date and is not a component.
 *
 * It falls back to the default rather than throwing, because a formatted figure
 * in the app's own language is a better failure than no figure at all — where a
 * missing translation is a visible key and worth the throw.
 */
export function getGlobalLocale(): SupportedLocale {
  const active = (i18nInstance?.global.locale as { value?: unknown } | undefined)?.value
  return typeof active === 'string' && isLocaleSupported(active) ? active : DEFAULT_LOCALE
}
